import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CloudIcon from '@mui/icons-material/Cloud';
import ShieldIcon from '@mui/icons-material/Shield';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CustomerServiceModal from '../components/contact/CustomerServiceModal';

const Security = () => {
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState(false);

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const securityFeatures = [
    {
      icon: <LockIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: '端到端加密',
      description: '所有文档传输和存储均采用自研加密技术，确保数据安全'
    },
    {
      icon: <VerifiedUserIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: '身份验证',
      description: '多重身份验证机制和访问控制，保护账户安全'
    },
    {
      icon: <CloudIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: '云端安全',
      description: '采用企业级云服务器，通过 ISO 27001 认证，提供最高级别的安全保障'
    },
    {
      icon: <ShieldIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: '隐私保护',
      description: '严格遵循 GDPR 和国内数据保护法规，绝不泄露用户隐私信息'
    }
  ];

  const certifications = [
    'ISO 27001 信息安全管理体系认证',
    '等保三级安全认证',
    'SSL/TLS 传输层安全协议',
    '定期安全审计和渗透测试'
  ];

  const dataProtection = [
    {
      question: '我们如何保护您的文档？',
      answer: '所有上传的文档都会进行端到端加密，存储在符合国际安全标准的服务器中。我们采用多层安全防护，包括防火墙、入侵检测系统和实时监控。'
    },
    {
      question: '文档会被保存多长时间？',
      answer: '翻译完成后，原始文档和翻译结果会在我们的安全服务器中保存 7 天，之后会被永久删除。您可以在此期间随时下载您的文件。'
    },
    {
      question: '谁可以访问我的文档？',
      answer: '您本人全权访问您的文档。我们的翻译系统采用自动化处理，无需人工干预。仅针对需要认证翻译，需授权指定的专业翻译人员可以访问您的文档，并且限制访问平台、访问方式、访问时间。'
    },
    {
      question: '如何确保翻译过程的安全性？',
      answer: '翻译过程完全在我们的安全环境中进行，采用隔离的计算资源。所有数据传输都经过加密，翻译引擎不会学习或记忆您的文档内容。'
    }
  ];

  return (
    <Box sx={{ py: 4 }}>
      {/* 页面标题 */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <SecurityIcon sx={{ fontSize: 60, color: theme.palette.primary.main, mb: 2 }} />
        <Typography 
          variant="h3" 
          component="h1" 
          sx={{ 
            fontWeight: 700,
            background: 'linear-gradient(90deg, #4353FF 0%, #6B78FF 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            mb: 2
          }}
        >
          安全保障
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          sx={{ maxWidth: 600, mx: 'auto' }}
        >
          我们采用企业级安全标准，全方位保护您的文档和隐私安全
        </Typography>
      </Box>

      {/* 安全特性 */}
      <Grid container spacing={4} sx={{ mb: 8 }}>
        {securityFeatures.map((feature, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: '100%',
                textAlign: 'center',
                p: 3,
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 35px rgba(0, 0, 0, 0.1)'
                }
              }}
            >
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  {feature.icon}
                </Box>
                <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* 安全认证 */}
      <Box sx={{ mb: 8, mt: 24 }}>
        <Typography variant="h3" sx={{ mb: 8, fontWeight: 600, textAlign: 'center', color: theme.palette.primary.main }}>
          安全认证与合规
        </Typography>
        <Card sx={{ p: 4, borderRadius: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                我们的安全认证
              </Typography>
              <List>
                {certifications.map((cert, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon 
                        sx={{ 
                          fontSize: 20, 
                          color: theme.palette.success.main 
                        }} 
                      />
                    </ListItemIcon>
                    <ListItemText 
                      primary={cert}
                      primaryTypographyProps={{
                        variant: 'body2'
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                技术安全措施
              </Typography>
              <List>
                {[
                  '24/7 安全监控和威胁检测',
                  '访问日志记录和审计',
                  '数据备份和灾难恢复',
                  '员工安全培训和背景调查',
                ].map((measure, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon 
                        sx={{ 
                          fontSize: 20, 
                          color: theme.palette.success.main 
                        }} 
                      />
                    </ListItemIcon>
                    <ListItemText 
                      primary={measure}
                      primaryTypographyProps={{
                        variant: 'body2'
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </Card>
      </Box>

      {/* 数据保护常见问题 */}
      <Box sx={{ mb: 8, mt: 24 }}>
        <Typography variant="h3" sx={{ mb: 8, fontWeight: 600, textAlign: 'center', color: theme.palette.primary.main }}>
          数据保护常见问题
        </Typography>
        {dataProtection.map((item, index) => (
          <Accordion 
            key={index}
            sx={{ 
              mb: 2, 
              borderRadius: 2,
              '&:before': {
                display: 'none',
              },
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ 
                px: 3,
                py: 2,
                '& .MuiAccordionSummary-content': {
                  margin: '12px 0'
                }
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                {item.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                {item.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* 联系我们 */}
      <Box sx={{ mt: 24, mb: 12, textAlign: 'center' }}>
        <Card sx={{ p: 4, borderRadius: 3, bgcolor: 'rgba(67, 83, 255, 0.05)' }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            还有安全方面的疑问？
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            我们的安全团队随时为您解答任何关于数据安全和隐私保护的问题
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleOpenModal}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              borderRadius: 3,
              boxShadow: '0 8px 25px rgba(67, 83, 255, 0.3)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 35px rgba(67, 83, 255, 0.4)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            联系安全团队
          </Button>
        </Card>
      </Box>

      {/* 客服弹窗 */}
      <CustomerServiceModal 
        open={modalOpen} 
        onClose={handleCloseModal} 
      />
    </Box>
  );
};

export default Security; 